import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import styles from './PrivacyPolicyPage.module.css';

export default function PrivacyPolicyPage() {
  const [searchParams] = useSearchParams();
  const deletionComplete = searchParams.get('deletion') === 'complete';

  return (
    <main className={styles.container}>
      <Link to="/" className={styles.backLink}>
        <ArrowLeft size={18} />
        Back to Peapil Health
      </Link>

      <header className={styles.header}>
        <p className={styles.eyebrow}>Peapil Health</p>
        <h1 className={styles.title}>Privacy Policy</h1>
        <p className={styles.updated}>Last updated: October 20, 2026</p>
      </header>

      {deletionComplete && (
        <div className={styles.notice} role="status">
          <strong>Account and core app data deletion complete.</strong> Your Peapil sign-in, profile, health-profile,
          and meal-plan data have been deleted. To request deletion of mobile-app food logs or uploaded meal photos,
          contact <a href="mailto:support@peapil.com?subject=Mobile%20food%20data%20deletion">privacy support</a>.
          Limited records may remain in protected backups or where legally required.
        </div>
      )}

      <div className={styles.notice} role="note">
        This plain-language notice describes the Peapil Health web and mobile apps based on the features reviewed.
        Store privacy disclosures must be kept consistent with the current app and its service providers.
      </div>

      <article className={styles.content}>
        <p className={styles.intro}>
          Peapil Publishing Inc. (“Peapil,” “we,” “us,” or “our”) operates the Peapil Health web and mobile
          apps. This policy explains what information the apps handle, why it is used, who may receive it, and
          how to contact us about your information.
        </p>

        <section className={styles.section}>
          <h2>1. Information we collect</h2>
          <p>Depending on which features you use, we may collect or process:</p>
          <ul>
            <li>
              <strong>Account and sign-in information:</strong> your email address, account identifier, and
              authentication/session information. You can sign in with an email code or password. Passwords
              are handled by our authentication provider and are not displayed to Peapil staff through the app.
            </li>
            <li>
              <strong>Health and nutrition profile information you provide:</strong> biological sex, age,
              height, current and target weight, nutrition or wellness goals, activity level, eating style,
              health considerations, eating challenges, foods to avoid, and food allergies. These details may
              be sensitive health-related information.
            </li>
            <li>
              <strong>Meal-planning information:</strong> meal-plan dates, selected recipes, and planned calorie
              totals associated with your account.
            </li>
            <li>
              <strong>Food logs and calorie-scan information:</strong> food entries you record, calorie and
              nutrient estimates, and associated dates. If you use the mobile calorie scanner, you can take or
              select a meal photo and upload it for scanning. Uploaded photos are stored in Supabase and used
              to provide the scan feature.
            </li>
            <li>
              <strong>Subscription and account status:</strong> subscription status and dates, and identifiers
              used to associate your Peapil account with subscription support. Payment card information is
              entered on the payment provider’s site, not in the Peapil Health web app.
            </li>
            <li>
              <strong>App activity and technical information:</strong> information needed to deliver the app,
              such as requests to load recipes and cookbooks. Hosting and service providers may also process
              technical logs such as IP address, browser or device details, timestamps, and request/error
              information for security, troubleshooting, and service operation.
            </li>
          </ul>
          <p>Recipe and cookbook content is provided by Peapil. We do not collect location data through either app.</p>
        </section>

        <section className={styles.section}>
          <h2>2. How we use information</h2>
          <ul>
            <li>Create and secure your account, sign you in, and provide customer support.</li>
            <li>Save and display your health profile and meal plan, and personalize nutrition and recipe features.</li>
            <li>Scan food photos you choose to upload, provide calorie and nutrient estimates, and support food logging.</li>
            <li>Show subscription access and help you manage your subscription.</li>
            <li>Operate, protect, troubleshoot, and improve the app and prevent misuse.</li>
            <li>Meet legal obligations and respond to valid legal requests.</li>
          </ul>
          <p>
            Health and nutrition information is used to provide the features you choose to use. The app provides
            general educational information, not medical diagnosis or treatment.
          </p>
        </section>

        <section className={styles.section}>
          <h2>3. Who receives information</h2>
          <p>
            We provide information to service providers only as needed to operate the service, and may disclose
            it when required by law or to protect users and the service. Providers used by the apps include:
          </p>
          <ul>
            <li>
              <strong>Supabase:</strong> authentication, database, and file storage for account, profile,
              health-profile, meal-plan, cookbook, and uploaded meal-photo data.
            </li>
            <li>
              <strong>Stripe:</strong> subscription checkout or account-management pages when you choose to use
              them. Stripe processes payment details on its own service; consult Stripe’s privacy notice for
              its handling of payment information.
            </li>
            <li>
              <strong>Website hosting and infrastructure providers:</strong> delivery and security of the app,
              including operational logs described above.
            </li>
          </ul>
          <p>
            The app also contains links to third-party websites, including subscription, offer, and app-store
            pages. If you follow one of those links, that provider’s privacy policy applies to information you
            give them. We do not control their services or privacy practices.
          </p>
        </section>

        <section className={styles.section}>
          <h2>4. Mobile app permissions and meal-photo scans</h2>
          <p>
            The mobile app may request camera or photo-library access when you choose to scan a meal photo.
            Photos you submit are uploaded to and stored in Supabase, and are used to scan the food and provide
            calorie and nutrient estimates. Food entries, estimates, and dates you choose to save may be
            associated with your account for food logging and history. You can decline camera or photo-library
            access, but photo-based scanning will not be available. We do not request location access or collect
            location data.
          </p>
        </section>

        <section className={styles.section}>
          <h2>5. Cookies and local storage</h2>
          <p>
            The web app stores authentication/session information in your browser’s local storage so that you
            can remain signed in. The mobile app also uses authentication/session information to keep you
            signed in. Linked websites may use their own cookies and similar technologies; their notices explain
            those practices.
          </p>
        </section>

        <section className={styles.section}>
          <h2>6. Retention and security</h2>
          <p>
            We keep account, health-profile, meal-plan, food-log, calorie-scan, and uploaded meal-photo data
            while it is needed to provide the service, meet legal obligations, resolve disputes, and enforce our
            agreements. Some information may remain in protected backups for a limited period after deletion.
            Retention periods can vary by data type and legal requirements.
          </p>
          <p>
            We use service providers and safeguards intended to protect information. No method of online
            transmission or storage can be guaranteed to be completely secure.
          </p>
        </section>

        <section className={styles.section}>
          <h2>7. Your choices and privacy requests</h2>
          <p>
            You can ask us to access, correct, or delete personal information associated with your account.
            If you can sign in, use the account-deletion control on your Profile &amp; Account page to delete
            your sign-in, Peapil profile, health-profile, meal-plan, and associated account records. To request
            deletion of mobile-app food logs or uploaded meal photos, email{' '}
            <a href="mailto:support@peapil.com?subject=Mobile%20food%20data%20deletion">support@peapil.com</a>.
            If you cannot sign in, email from the address associated with your account and request deletion.
            We will verify account ownership and complete the request or contact you if more information is needed.
            Some information may be retained where the law permits or requires it, and protected backups may
            persist for a limited period.
          </p>
          <p>
            You can also manage your subscription through the subscription-management link in your account.
            Cancelling a subscription does not by itself delete your Peapil Health account or profile data.
          </p>
        </section>

        <section className={styles.section}>
          <h2>8. Children</h2>
          <p>
            Peapil Health is not intended for children under 13, and we do not knowingly collect personal
            information from children under 13. The health-profile questionnaire is designed for adults. If you
            believe a child has provided information, contact us so we can review and address it.
          </p>
        </section>

        <section className={styles.section}>
          <h2>9. International processing</h2>
          <p>
            Peapil and its service providers may process information in countries other than the country where
            you live. Privacy protections and legal requirements may differ between those locations.
          </p>
        </section>

        <section className={styles.section}>
          <h2>10. Changes to this policy</h2>
          <p>
            We may update this policy when our practices or legal requirements change. We will post the
            revised version here and update the date above. Please review it periodically.
          </p>
        </section>

        <section className={styles.section}>
          <h2>11. Contact</h2>
          <p>
            Peapil Publishing Inc. is responsible for this policy. For privacy questions or requests, email{' '}
            <a href="mailto:support@peapil.com">support@peapil.com</a>. For general app support, you can also
            contact <a href="mailto:support@peapilhealth.com">support@peapilhealth.com</a>.
          </p>
        </section>

        <section className={`${styles.section} ${styles.references}`}>
          <h2>Platform privacy guidance</h2>
          <p>
            This notice is organized around the transparency principles in Google Play’s User Data policy and
            Apple’s App Privacy Details. Store privacy declarations must still be completed separately and
            kept consistent with the current app and its third-party services.
          </p>
          <a href="https://support.google.com/googleplay/android-developer/answer/10144311" target="_blank" rel="noopener noreferrer">
            Google Play User Data policy <ExternalLink size={14} />
          </a>
          <a href="https://developer.apple.com/app-store/app-privacy-details/" target="_blank" rel="noopener noreferrer">
            Apple App Privacy Details <ExternalLink size={14} />
          </a>
        </section>

        <nav className={styles.bottomLinks} aria-label="Legal pages">
          <Link to="/legal">Legal information</Link>
          <a href="https://peapilpublishing.com/policies/terms-of-service" target="_blank" rel="noopener noreferrer">
            Terms of Service
          </a>
          <a href="mailto:support@peapil.com">Contact privacy support</a>
        </nav>
      </article>
    </main>
  );
}
